import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { VersionPipe } from '../../shared/pipes/version.pipe';
import { environment } from 'src/environments/environment';

interface PackageVersion {
  id: string;
  version: string;
  normalizedVersion: string;
  publishDate: string;
}
interface Package {
  id: string;
  normalizedName: string;
  versions: PackageVersion[];
}
interface Feed {
  count: number;
  value: Package[];
}

@Component({
  selector: 'app-new-comparison',
  templateUrl: './new-comparison.component.html',
  styleUrls: ['./new-comparison.component.css']
})
export class NewComparisonComponent implements OnInit {
  visible = false;
  customers: Package[] = [];
  templateVersions: PackageVersion[] = [];
  customerConfigVersions: PackageVersion[] = [];
  secondCustomerConfigVersions: PackageVersion[] = [];

  comparisonForm!: FormGroup;
  @Output() reloadGrid = new EventEmitter<void>();

  constructor(private http: HttpClient, private versionPipe: VersionPipe) {}

  ngOnInit(): void {
    this.comparisonForm = new FormGroup({
      comparisonType: new FormControl<'template' | 'customer'>('template'),
      customer: new FormControl<string | null>(null, Validators.required),
      customerConfigVersion: new FormControl<string | null>(null, Validators.required),
      targetVersion: new FormControl<string | null>(null, Validators.required),
      secondCustomer: new FormControl<string | null>(null),
      secondCustomerConfigVersion: new FormControl<string | null>(null),
    });

    this.comparisonForm.get('comparisonType')!.valueChanges.subscribe(type => {
      const tv = this.comparisonForm.get('targetVersion')!;
      const sc = this.comparisonForm.get('secondCustomer')!;
      const scv = this.comparisonForm.get('secondCustomerConfigVersion')!;

      if (type === 'template') {
        tv.setValidators([Validators.required]);
        sc.clearValidators();
        scv.clearValidators();
      } else {
        tv.clearValidators();
        sc.setValidators([Validators.required]);
        scv.setValidators([Validators.required]);
      }
      tv.updateValueAndValidity();
      sc.updateValueAndValidity();
      scv.updateValueAndValidity();
    });

    this.fetchCustomers(
      `${environment.configPackagingApiConfig.uri}/config-xmls/packages?api-version=7.1-preview.1&includeAllVersions=true`
    );
  }

  fetchCustomers(url: string) {
    this.http.get<Feed>(url).subscribe(feed => {
      this.customers = feed.value.filter(c => c.normalizedName !== 'templates');
      const tmpl = feed.value.find(c => c.normalizedName === 'templates');
      this.templateVersions = tmpl?.versions || [];
    });
  }

  onCustomerSelection(name: string) {
    const pkg = this.customers.find(c => c.normalizedName === name)!;
    this.customerConfigVersions = pkg.versions.map(v => ({
      ...v,
      version: this.versionPipe.transform(v.version)
    }));
    this.comparisonForm.patchValue({ customerConfigVersion: null });
  }

  onSecondCustomerSelection(name: string) {
    const pkg = this.customers.find(c => c.normalizedName === name)!;
    this.secondCustomerConfigVersions = pkg.versions.map(v => ({
      ...v,
      version: this.versionPipe.transform(v.version)
    }));
    this.comparisonForm.patchValue({ secondCustomerConfigVersion: null });
  }

  showCreateNewComparisonDialog(): void {
    this.visible = true;
  }
  hide(): void {
    this.visible = false;
    this.comparisonForm.reset({ comparisonType: 'template' });
  }
  onHide(): void {
    this.comparisonForm.markAsPristine();
  }
  canSubmit(): boolean {
    return this.comparisonForm.valid;
  }

  onSubmit(): void {
    if (!this.canSubmit()) {
      this.comparisonForm.markAllAsTouched();
      return;
    }

    const {
      comparisonType,
      customer,
      customerConfigVersion,
      targetVersion,
      secondCustomer,
      secondCustomerConfigVersion
    } = this.comparisonForm.value as any;

    // Map always into customerA/versionA and customerB/versionB
    let customerA = customer!;
    let versionA = customerConfigVersion!;
    let customerB: string;
    let versionB: string;

    if (comparisonType === 'template') {
      customerB = 'templates';
      versionB = targetVersion!;
    } else {
      customerB = secondCustomer!;
      versionB = secondCustomerConfigVersion!;
    }

    const payload = {
      definition: { id: 75 },
      templateParameters: {
        customerA,
        versionA,
        customerB,
        versionB
      }
    };

    this.http
      .post(
        `${environment.buildApiConfig.uri}/?api-version=7.1-preview.7`,
        payload
      )
      .subscribe(() => {
        this.visible = false;
        this.reloadGrid.emit();
      });
  }
}
